import type { VendorReading } from '@/api/types'
import { mindsetBloodOxygen, mindsetBloodPressure, mindsetPulseRate, mindsetRespirationRate } from './mindset-result'
import type { MindsetFinalValues } from './mindset-types'
import { shenAiBloodPressure } from './shenai-result'
import type { ShenAiMeasurementResults } from './shenai-types'

/**
 * Each vendor's OWN reading, for the `vendorReadings` a hybrid scan submits beside its composite
 * (KAN-3; docs/decisions.md D-62).
 *
 * Deliberately NOT `toSubmitRequestFrom…`: those answer "is this a submission on its own", and refuse a
 * reading with no pulse or no pressure. Here the question is "what did this vendor measure", and a
 * vendor that produced a pulse and a breathing rate but no pressure measured something - recording it
 * as `measured: false` would be the partial-scan marker lying. So each vital is read on its own, by the
 * same per-vital rules the single-vendor mappers use (rounded, non-positive is absent, `_MD` before
 * `_GW`, a class carried as a class), and `measured` means "at least one vital arrived".
 */
export function shenAiVendorReading(results: ShenAiMeasurementResults | null | undefined): VendorReading {
  const pressure = shenAiBloodPressure(results)
  return reading('ShenAi', {
    ...(pressure ?? {}),
    hr: positive(results?.heart_rate_bpm),
    rr: positive(results?.breathing_rate_bpm),
  })
}

export function mindsetVendorReading(values: MindsetFinalValues | null | undefined): VendorReading {
  if (!values) return reading('Mindset', {})
  return reading('Mindset', {
    ...(mindsetBloodPressure(values) ?? {}),
    hr: positive(mindsetPulseRate(values)),
    rr: positive(mindsetRespirationRate(values)),
    spo2: positive(mindsetBloodOxygen(values)),
  })
}

type Vitals = Omit<VendorReading, 'vendor' | 'measured'>

function reading(vendor: VendorReading['vendor'], vitals: Vitals): VendorReading {
  const present = Object.fromEntries(
    Object.entries(vitals).filter(([, value]) => value !== undefined && value !== null),
  ) as Vitals
  return { vendor, measured: Object.keys(present).length > 0, ...present }
}

function positive(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : undefined
}
